import * as debounce from 'lodash.debounce'
import * as loadjs from 'loadjs'
import { lazyTimeout } from '../constants'
import { ExperimentFlag } from '../enums/ExperimentFlag'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import { Device } from '../helpers'
import { Scroll } from '../helpers/Scroll'
import PrebidFacade from '../prebidFacade/PrebidFacade'
import { Slot } from '../slots/Slot'
export const TRIGGER_SUFFIX = 'waypoint'
export const DEFAULT_MOBILE_OFFSET = 2400
export const DEFAULT_DESKTOP_OFFSET = 1600
export let maxVelocity

export default class Lazyable {
  // These statics let us debounce bid requests from lazy slots.
  static lazySlotsToRequest: Slot[] = []
  static debounceRequestBids: Function

  observer: IntersectionObserver

  constructor(private slot: Slot, private prebidFacade: PrebidFacade) {
    this.createWaypoint()

    // Set up the static Lazyable.debounceRequestBids if it doesn't already exist
    if (!Lazyable.debounceRequestBids) {
      Lazyable.debounceRequestBids = debounce(
        () => {
          // Let's find the largest timeout among the slots, and use that.
          const timeouts = Lazyable.lazySlotsToRequest.map(
            (slot) => slot.timeout || lazyTimeout()
          )
          const maxTimeout = Math.max(...timeouts)

          prebidFacade.requestBids(Lazyable.lazySlotsToRequest, maxTimeout)
          Lazyable.lazySlotsToRequest = []
        },
        100,
        { maxWait: 500 }
      )
    }
  }

  private createWaypoint(): void {
    const slot: Slot = this.slot

    const offset = slot.offset
      ? slot.offset
      : Device.isMobileOrTablet
      ? DEFAULT_MOBILE_OFFSET
      : DEFAULT_DESKTOP_OFFSET

    const threshold = 0.0
    const options = {
      rootMargin: `0px 0px ${offset}px 0px`,
      threshold
    }

    const callback = (entries) => {
      const velocity = Scroll.delta
      maxVelocity = getMaxVelocity(slot)
      entries.forEach((entry) => {
        if ((entry.isIntersecting || slot.sticky) && velocity < maxVelocity) {
          this.observer.disconnect()
          loadjs.ready(LoadJsBundle.nonLazyRequestsLoaded, () => {
            // Wait for NonLazy Ads to have loaded before requesting new bids.
            slot.setTargeting({ velocity, maxVelocity })
            Lazyable.lazySlotsToRequest.push(slot)
            Lazyable.debounceRequestBids()
          })
        }
      })
    }

    this.observer = new IntersectionObserver(callback, options)
    this.observer.observe(
      this.slot.wrapper.previousElementSibling ||
        (this.slot.wrapper.parentElement as HTMLElement)
    )
  }
}

function getMaxVelocity(slot: Slot): number {
  return (
    maxVelocity ||
    (parseInt(
      slot.model.experiment.activate(ExperimentFlag.velocityCheck) || '0',
      10
    ) ||
      Infinity)
  )
}
